import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const RAIL_WIDTH_COLLAPSED = rem(72);

/**
 * The footprint the rail keeps in the page. The expanded rail overlays the content rather than
 * pushing it, so this is the gutter a layout reserves beside the navigation.
 */
export const uiSideNavRailWidth = RAIL_WIDTH_COLLAPSED;

const RAIL_WIDTH_EXPANDED = rem(256);
const DRAWER_WIDTH = rem(320);
const WORKSPACE_MENU_WIDTH = rem(360);

const railRow = { minWidth: RAIL_WIDTH_EXPANDED };

const hairline = {
  background: 'border/inverse',
  content: '""',
  height: '1',
  opacity: 0.2,
} as const;

const uiSideNavSlotRecipe = defineSlotRecipe({
  className: 'ui-side-nav',
  slots: [
    'backdrop',
    'body',
    'closeButton',
    'content',
    'footer',
    'footerHeading',
    'footerSeparator',
    'item',
    'itemIcon',
    'itemIndicator',
    'itemLabel',
    'link',
    'linkIcon',
    'linkIconBlock',
    'linkLabel',
    'linkSecondaryText',
    'linkStatusDot',
    'linkTextBlock',
    'positioner',
    'root',
    'workspace',
    'workspaceChevron',
    'workspaceControl',
    'workspaceLabel',
    'workspaceMenu',
    'workspaceRoot',
    'workspaceTextBlock',
    'workspaceValue',
  ],
  base: {
    root: {
      background: 'sys/ui/sidenav/bg',
      display: 'flex',
      flexDirection: 'column',
      height: 'full',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    workspace: {
      alignItems: 'center',
      background: 'sys/ui/sidenav/item/bg',
      // Preflight zeroes every border width but not the radius a native button can still draw.
      borderRadius: 0,
      color: 'text/on-color',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      gap: '16',
      height: rem(72),
      minHeight: rem(72),
      position: 'relative',
      textAlign: 'start',
      width: 'full',
      _after: {
        ...hairline,
        insetBlockEnd: 0,
        insetInline: 0,
        position: 'absolute',
      },
      _hover: {
        background: 'sys/ui/sidenav/item/bg-hover',
        _active: {
          background: 'sys/ui/sidenav/item/bg-active',
        },
      },
      _active: {
        background: 'sys/ui/sidenav/item/bg-active',
      },
    },
    workspaceRoot: {
      flexShrink: 0,
      width: 'full',
    },
    workspaceTextBlock: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      minWidth: 0,
    },
    workspaceLabel: {
      opacity: 0.6,
      textStyle: 'heading/h6',
      textTransform: 'uppercase',
    },
    workspaceValue: {
      opacity: 0.8,
      textStyle: 'body/lg/regular',
      truncate: true,
    },
    workspaceChevron: {
      color: 'text/on-color',
      flexShrink: 0,
      transition: 'rotate 150ms ease-out',
      _open: {
        rotate: '180deg',
      },
      _motionReduce: {
        transition: 'none',
      },
    },
    body: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      minHeight: 0,
    },
    item: {
      alignItems: 'center',
      background: 'sys/ui/sidenav/item/bg',
      color: 'sys/ui/sidenav/item/fg',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      overflow: 'hidden',
      paddingInlineEnd: '8',
      paddingInlineStart: '24',
      position: 'relative',
      textDecoration: 'none',
      width: 'full',
      _hover: {
        background: 'sys/ui/sidenav/item/bg-hover',
        color: 'sys/ui/sidenav/item/fg-hover',
        _active: {
          background: 'sys/ui/sidenav/item/bg-active',
        },
      },
      _active: {
        background: 'sys/ui/sidenav/item/bg-active',
        color: 'sys/ui/sidenav/item/fg-active',
      },
      _selected: {
        background: 'sys/ui/sidenav/item/bg-selected',
        color: 'sys/ui/sidenav/item/fg-active',
        _hover: {
          background: 'sys/ui/sidenav/item/bg-hover',
          _active: {
            background: 'sys/ui/sidenav/item/bg-active',
          },
        },
        _active: {
          background: 'sys/ui/sidenav/item/bg-active',
        },
      },
      _focusVisible: {
        outlineOffset: 'calc(var(--focus-ring-width) * -1)',
      },
    },
    itemIcon: {
      flexShrink: 0,
    },
    itemLabel: {
      flex: 1,
      minWidth: 0,
      textStyle: 'body/lg/semibold',
      truncate: true,
    },
    itemIndicator: {
      background: 'sys/ui/sidenav/item/indicator',
      insetBlock: 0,
      insetInlineStart: 0,
      position: 'absolute',
      width: '4',
    },
    footer: {
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      marginBlockStart: 'auto',
    },
    footerSeparator: {
      flexShrink: 0,
      paddingBlock: '8',
      paddingInline: '12',
      _before: {
        ...hairline,
        display: 'block',
        width: 'full',
      },
    },
    footerHeading: {
      color: 'text/on-color',
      opacity: 0.6,
      paddingBlockEnd: '12',
      paddingInline: '24',
      textStyle: 'heading/h6',
      textTransform: 'uppercase',
    },
    link: {
      alignItems: 'center',
      background: 'sys/ui/sidenav/item/bg',
      borderWidth: 0,
      color: 'sys/ui/sidenav/item/fg',
      cursor: 'pointer',
      display: 'flex',
      flexShrink: 0,
      overflow: 'hidden',
      paddingBlock: '12',
      paddingInline: '24',
      textAlign: 'start',
      textDecoration: 'none',
      width: 'full',
      _hover: {
        background: 'sys/ui/sidenav/item/bg-hover',
        color: 'sys/ui/sidenav/item/fg-hover',
        _active: {
          background: 'sys/ui/sidenav/item/bg-active',
        },
      },
      _active: {
        background: 'sys/ui/sidenav/item/bg-active',
        color: 'sys/ui/sidenav/item/fg-active',
      },
      _focusVisible: {
        outlineOffset: 'calc(var(--focus-ring-width) * -1)',
      },
      '&[data-with-secondary]': {
        paddingBlock: '8',
      },
    },
    linkTextBlock: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      minWidth: 0,
      '[data-with-secondary] &': {
        marginBlock: '2',
      },
    },
    linkLabel: {
      flex: 1,
      minWidth: 0,
      textStyle: 'body/lg/regular',
      truncate: true,
      '[data-with-secondary] &': {
        flex: 'none',
        textStyle: 'body/md/semibold',
      },
    },
    linkSecondaryText: {
      textStyle: 'body/sm/regular',
      truncate: true,
    },
    linkIcon: {
      flexShrink: 0,
    },
    linkIconBlock: {
      display: 'flex',
      flexShrink: 0,
      position: 'relative',
    },
    linkStatusDot: {
      borderRadius: 'full',
      height: '8',
      insetBlockEnd: '-2',
      insetInlineEnd: '-4',
      position: 'absolute',
      width: '8',
      '&[data-status="neutral"]': {
        background: 'sys/ui/sidenav/badge/neutral',
      },
      '&[data-status="success"]': {
        background: 'sys/ui/sidenav/badge/green',
      },
      '&[data-status="critical"]': {
        background: 'sys/ui/sidenav/badge/red',
      },
    },
    backdrop: {
      background: 'utilities/overlay',
      inset: 0,
      position: 'fixed',
      zIndex: 'calc({zIndex.dialogOverlay} + var(--layer-index, 0))',
      _open: {
        animationDuration: 'slow',
        animationName: 'fade-in',
      },
      _closed: {
        animationDuration: 'moderate',
        animationName: 'fade-out',
      },
    },
    positioner: {
      alignItems: 'stretch',
      display: 'flex',
      inset: 0,
      // The drawer comes in from the leading edge, unlike every other drawer in the library.
      justifyContent: 'flex-start',
      overscrollBehaviorY: 'none',
      position: 'fixed',
      zIndex: 'calc({zIndex.dialog} + var(--layer-index, 0))',
    },
    content: {
      background: 'sys/ui/sidenav/bg',
      boxShadow: 'elevation/lg',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100dvh',
      outline: 'none',
      // The close button hangs off the trailing edge, so the panel must not clip it.
      overflow: 'visible',
      position: 'relative',
      width: DRAWER_WIDTH,
      // 320 for the panel plus the 55 the close button column occupies, which is the 375 the
      // design frame is drawn at. Narrower phones give the panel up, not the button.
      maxWidth: 'calc(100vw - 55px)',
      _open: {
        animationDuration: 'slowest',
        animationName: {
          base: 'slide-from-left-full, fade-in',
          _rtl: 'slide-from-right-full, fade-in',
        },
        animationTimingFunction: 'ease-in-smooth',
      },
      _closed: {
        animationDuration: 'slower',
        animationName: {
          base: 'slide-to-left-full, fade-out',
          _rtl: 'slide-to-right-full, fade-out',
        },
        animationTimingFunction: 'ease-in-smooth',
      },
    },
    closeButton: {
      alignItems: 'center',
      display: 'flex',
      height: rem(72),
      insetBlockStart: 0,
      insetInlineStart: '100%',
      paddingInlineStart: '8',
      position: 'absolute',
    },
  },

  variants: {
    layout: {
      rail: {
        root: {
          paddingBlockEnd: '12',
          // Opening is slower to reach than closing, so a pointer crossing the rail on its way
          // somewhere else does not drag it open.
          transition: 'width 100ms ease-out',
          transitionDelay: '50ms',
          width: RAIL_WIDTH_COLLAPSED,
          _motionReduce: {
            transition: 'none',
          },
          '&[data-expanded]': {
            transitionDelay: '100ms',
            width: RAIL_WIDTH_EXPANDED,
          },
        },
        workspace: { ...railRow, paddingInline: '16' },
        workspaceControl: railRow,
        workspaceMenu: { width: WORKSPACE_MENU_WIDTH },
        body: { overflowX: 'hidden', overflowY: 'auto', scrollbar: 'hidden' },
        item: { ...railRow, gap: '24', paddingBlock: '16' },
        link: { ...railRow, gap: '24' },
        footerSeparator: {
          '[data-expanded] &': {
            paddingInline: '16',
          },
        },
        footerHeading: railRow,
      },
      drawer: {
        root: {
          overflowX: 'hidden',
          overflowY: 'auto',
          width: 'full',
        },
        workspaceRoot: {
          background: 'sys/ui/sidenav/bg',
          insetBlockStart: 0,
          position: 'sticky',
          zIndex: 1,
        },
        workspace: {
          paddingBlock: '16',
          paddingInlineEnd: '24',
          paddingInlineStart: '16',
        },
        item: { gap: '12', paddingBlock: '16' },
        link: { gap: '12' },
        footer: {
          paddingBlockEnd: '16',
          paddingBlockStart: '12',
          position: 'relative',
        },
        footerHeading: {
          paddingBlockStart: '12',
          '&[data-section]': {
            marginBlockStart: '12',
            paddingBlockStart: '24',
            position: 'relative',
            _before: {
              ...hairline,
              insetBlockStart: 0,
              insetInline: 0,
              position: 'absolute',
            },
          },
        },
        footerSeparator: {
          insetBlockStart: 0,
          insetInline: 0,
          paddingBlock: 0,
          paddingInline: 0,
          position: 'absolute',
        },
        workspaceMenu: {
          // The panel is the whole screen, so the menu takes the height it can get rather than
          // the desktop cap, which would leave a short list floating in a tall drawer.
          maxHeight: 'var(--available-height)',
          '--select-menu-list-max-height': 'none',
        },
        body: { flex: '1 0 auto', paddingBlock: '8' },
      },
    },
  },

  defaultVariants: {
    layout: 'rail',
  },
});

export default uiSideNavSlotRecipe;
